import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "./../../axios";
import { timeHelper, dateHelper } from "../../Utils/datetime_helper";
import { toast } from "react-toastify";
import {
  BookType,
  Calendar,
  ClockFadingIcon,
  Trophy,
  FileText,
  MessageSquare,
  Layers,
  Target,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";

function CompletedCompetencyReport() {
  const location = useLocation();
  const navigate = useNavigate();

  // State se data utha rahe hain jo Edit se aaya
  const { state } = location;
  const competencyDetails =
    state?.competencyDetails || state?.assessment || null;
  const competencyIdParam = state?.assessmentId || competencyDetails?._id;

  // ─── States ──────────────────────────────────────
  const [loadingData, setLoadingData] = useState(true);
  const [competencyData, setCompetencyData] = useState(competencyDetails);
  const [studentList, setStudentList] = useState([]);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("");

  // ─── Local Storage Data ──────────────────────────
  const schoolId = JSON.parse(localStorage.getItem("schoolId"));

  // ─── Extract Competency Details ──────────────────
  const rubricDetails =
    competencyData?.rubricScaleDetails || competencyData?.rubricScaleId;
  const evaluationTypeName =
    competencyData?.evaluationType?.name ||
    (rubricDetails ? "Rubrics + Remarks" : "Remarks");

  const hasRubrics =
    evaluationTypeName?.toLowerCase().includes("rubrics") && rubricDetails;
  const hasRemarks = evaluationTypeName?.toLowerCase().includes("remarks");
  const rubricItems = rubricDetails?.rubricItems || [];

  // ─── Fetch Data on Mount ─────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (!competencyIdParam) {
        toast.error("No Competency ID found!");
        setLoadingData(false);
        return;
      }

      setShowLoadingDialog(true);
      setLoadingMessage("Loading completed evaluation data...");
      setLoadingProgress(20);

      try {
        // 1️⃣ API 1: GET COMPETENCY EVALUATION DETAILS
        setLoadingProgress(40);
        setLoadingMessage("Fetching evaluation details...");

        const evalRes = await axios.get(
          `/api/v1/competency/evaluations/${competencyIdParam}`,
        );
        console.log("✅ Evaluation API Response:", evalRes?.data);

        if (evalRes?.data?.success) {
          const fetchedEvalData =
            evalRes?.data?.data?.evaluation || evalRes?.data?.data || null;
          if (fetchedEvalData) {
            setCompetencyData((prev) => ({ ...prev, ...fetchedEvalData }));
          }
        }

        // 2️⃣ API 2: GET STUDENTS
        setLoadingProgress(70);
        setLoadingMessage("Fetching student evaluations...");

        const studentRes = await axios.get(
          `/api/v1/competency/evaluations/${competencyIdParam}/students`,
        );
        console.log("✅ Students API Response:", studentRes?.data);

        const studentData =
          studentRes?.data?.data?.students || studentRes?.data?.data || [];

        // Map to UI format
        const students = studentData.map((student) => ({
          id: student.studentId,
          name: student.name || "Unknown",
          rollNumber: student.rollNo || "-",
          rubricValue: student.rubricValue || "",
          remark: student.remark || "",
        }));

        // Sort by Roll Number
        students.sort((a, b) => {
          const numA = parseFloat(a.rollNumber) || 0;
          const numB = parseFloat(b.rollNumber) || 0;
          return numA - numB;
        });

        setStudentList(students);
        setLoadingProgress(100);
        setLoadingMessage("Data loaded successfully!");

        // Small delay to show 100%
        await new Promise((resolve) => setTimeout(resolve, 400));
      } catch (error) {
        console.error("❌ Error fetching data:", error);
        toast.error(error?.response?.data?.message || "Failed to load data");
      } finally {
        setLoadingData(false);
        setShowLoadingDialog(false);
      }
    };

    fetchData();
  }, [competencyIdParam]);

  // ─── Get Column Count ──────────────────────────
  const getColumnCount = () => {
    let count = 2;
    if (hasRubrics) count++;
    if (hasRemarks) count++;
    return count;
  };

  // ─── Get Status Badge Color ────────────────────
  const getStatusBadgeStyle = (status) => {
    if (status === "completed") {
      return {
        backgroundColor: "#d1fae5",
        color: "#065f46",
        label: "Completed",
      };
    } else if (status === "draft") {
      return {
        backgroundColor: "#fef3c7",
        color: "#92400e",
        label: "Draft",
      };
    } else {
      return {
        backgroundColor: "#f3f4f6",
        color: "#6b7280",
        label: status || "Unknown",
      };
    }
  };

  // ─── Render Rubric Value (Read-Only) ────────────
  const renderRubricValue = (student) => {
    const value = student.rubricValue;
    if (!value) {
      return <span style={{ color: "#94a3b8", fontSize: "13px" }}>—</span>;
    }

    const rubricItem = rubricItems.find((r) => r.value === value);
    return (
      <div>
        <span
          style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "6px",
            backgroundColor: "#fef3c7",
            color: "#92400e",
            fontSize: "13px",
            fontWeight: 600,
          }}
        >
          {value}
        </span>
        {rubricItem?.description && (
          <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
            {rubricItem.description}
          </div>
        )}
      </div>
    );
  };

  // ─── Render Remark (Read-Only) ──────────────────
  const renderRemarkValue = (student) => {
    if (!student.remark) {
      return <span style={{ color: "#94a3b8", fontSize: "13px" }}>—</span>;
    }
    return (
      <div
        style={{
          padding: "6px 10px",
          backgroundColor: "#f8fafc",
          borderRadius: "6px",
          fontSize: "13px",
          color: "#1e293b",
          maxWidth: "100%",
          wordWrap: "break-word",
        }}
      >
        {student.remark}
      </div>
    );
  };

  // ─── Loading State ──────────────────────────────
  if (loadingData) {
    return (
      <div
        className="w-100 d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div className="d-flex flex-column align-items-center gap-2">
          <Loader2 className="spinner-border text-warning" size={30} />
          <span className="text-muted small">Loading Completed Data...</span>
        </div>
      </div>
    );
  }

  // ─── Main Render ──────────────────────────────────
  return (
    <div
      className="w-100 orange-scrollBar"
      style={{ overflowY: "hidden", height: "calc(100vh - 100px)" }}
    >
      {/* ─── Loading Dialog ──────────────────────── */}
      <Dialog
        open={showLoadingDialog}
        disableEscapeKeyDown
        PaperProps={{
          style: {
            borderRadius: "20px",
            padding: "0",
            minWidth: "420px",
            maxWidth: "460px",
            boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              height: "4px",
              width: "100%",
              background:
                "linear-gradient(90deg, #22c55e, #4ade80, #86efac, #22c55e)",
              backgroundSize: "300% 100%",
              animation: "shimmer 2s infinite linear",
            }}
          />
          <Box sx={{ textAlign: "center", py: 1, px: 1 }}>
            <Box sx={{ position: "relative", display: "inline-flex", mb: 1 }}>
              <CircularProgress
                variant="determinate"
                value={loadingProgress}
                size={80}
                thickness={3.5}
                sx={{
                  color: "#22c55e",
                  "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#22c55e",
                    fontSize: "18px",
                    lineHeight: 1,
                  }}
                >
                  {loadingProgress}%
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                mb: 0.5,
                fontSize: "17px",
              }}
            >
              {loadingMessage || "Loading..."}
            </Typography>
            <Box sx={{ width: "100%", mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 8,
                  borderRadius: "10px",
                  backgroundColor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: "10px",
                    background: "linear-gradient(90deg, #22c55e, #4ade80)",
                    transition: "transform 0.4s ease-in-out",
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Header ──────────────────────────────── */}
      <div className="mb-3 pt-3">
        <div
          className="d-flex align-items-center gap-2 cursor-pointer"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/offline-performance/competency")}
        >
          <div>
            <ArrowLeft size={20} color="#64748b" />
          </div>
          <div className="d-flex flex-column gap-1">
            <span
              className="head_manual"
              style={{ fontSize: "18px", fontWeight: 600 }}
            >
              Completed Competency Evaluation
            </span>
            <span className="fw-small text-muted" style={{ fontSize: "10px" }}>
              Dashboard / Performance / Completed Evaluation
            </span>
          </div>
        </div>
      </div>

      {/* ─── Main Card ───────────────────────────── */}
      <div
        className="w-100 py-3 px-4 rounded-3"
        style={{
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 190px)",
        }}
      >
        {/* ─── Top Info Bar ──────────────────────── */}
        <div style={{ flexShrink: 0 }}>
          <div className="class-section-subject d-flex align-items-start flex-column gap-2 ml-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                className="fs-5 poppins-semibold"
                style={{ color: "#1e293b" }}
              >
                <span>
                  {competencyData?.standardId?.name ||
                    competencyData?.standard_id?.name ||
                    "N/A"}{" "}
                  -{" "}
                  {competencyData?.sectionId?.name ||
                    competencyData?.section_id?.name ||
                    "N/A"}
                </span>
              </div>

              {/* Status Badge */}
              <span
                className="badge"
                style={{
                  backgroundColor: getStatusBadgeStyle(competencyData?.status)
                    .backgroundColor,
                  color: getStatusBadgeStyle(competencyData?.status).color,
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 14px",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                {competencyData?.status === "completed" ? (
                  <CheckCircle size={12} />
                ) : (
                  <XCircle size={12} />
                )}
                {getStatusBadgeStyle(competencyData?.status).label}
              </span>

              <span
                className="badge"
                style={{
                  backgroundColor: "#6366f1",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 12px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Layers size={12} />{" "}
                {competencyData?.domain_id?.name || "Domain"}
              </span>
              <span
                className="badge"
                style={{
                  backgroundColor: "#8b5cf6",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 12px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Target size={12} />{" "}
                {competencyData?.competency_id?.name || "Competency"}
              </span>
              <span
                className="badge"
                style={{
                  backgroundColor: "#f77f00",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 12px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Trophy size={12} /> {rubricDetails?.name || "Rubric"}
              </span>
              <span
                className="badge"
                style={{
                  backgroundColor:
                    hasRubrics && hasRemarks
                      ? "#0ea5e9"
                      : hasRubrics
                        ? "#10b981"
                        : "#f59e0b",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 12px",
                  borderRadius: "20px",
                }}
              >
                {evaluationTypeName}
              </span>
            </div>

            <div className="d-flex align-items-center w-100 gap-4 flex-wrap mt-1">
              <div
                className="title-text"
                style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}
              >
                <BookType
                  size={16}
                  className="me-2"
                  style={{ color: "#f77f00" }}
                />{" "}
                {competencyData?.name || "Untitled"}
              </div>
              {competencyData?.schedule_date && (
                <>
                  <div className="d-flex align-items-center gap-2">
                    <Calendar size={16} style={{ color: "#64748b" }} />
                    <span
                      className="poppins-semibold"
                      style={{ fontSize: "13px", color: "#475569" }}
                    >
                      {dateHelper(competencyData.schedule_date)}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <ClockFadingIcon size={16} style={{ color: "#64748b" }} />
                    <span
                      className="poppins-semibold"
                      style={{ fontSize: "13px", color: "#475569" }}
                    >
                      {timeHelper(competencyData.schedule_time)}
                    </span>
                  </div>
                </>
              )}
              <div className="d-flex align-items-center gap-2">
                <FileText size={16} style={{ color: "#64748b" }} />
                <span
                  className="poppins-semibold"
                  style={{ fontSize: "13px", color: "#475569" }}
                >
                  {competencyData?.assessmentEventId?.name ||
                    competencyData?.assessmentEvent_id?.name ||
                    competencyData?.reportCycle_id?.name ||
                    "Assessment Event"}
                </span>
              </div>
            </div>
          </div>

          {competencyData?.description && (
            <div className="ml-3 mt-2">
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                <span style={{ fontWeight: 600, color: "#334155" }}>
                  Description:
                </span>{" "}
                {competencyData.description}
              </div>
            </div>
          )}
        </div>

        {/* ─── Student Table (READ-ONLY) ────────── */}
        <div
          className="mid-student-table"
          style={{ flex: 1, overflowY: "auto", minHeight: 0 }}
        >
          <div className="table-container-offlineSub">
            <div className="scrollable-table-wrapper">
              <table className="student-table-offlineSub">
                <thead style={{ position: "sticky", top: "0", zIndex: 10 }}>
                  <tr>
                    <th style={{ width: "25%" }}>Name</th>
                    <th style={{ width: "15%" }}>Roll No.</th>
                    {hasRubrics && (
                      <th style={{ width: hasRemarks ? "30%" : "50%" }}>
                        <div className="d-flex align-items-center gap-2">
                          <Trophy size={14} /> Rubric Level
                        </div>
                      </th>
                    )}
                    {hasRemarks && (
                      <th style={{ width: hasRubrics ? "30%" : "60%" }}>
                        <div className="d-flex align-items-center gap-2">
                          <MessageSquare size={14} /> Remarks
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {studentList.map((student, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: 500, color: "#1e293b" }}>
                        {student.name}
                      </td>
                      <td style={{ color: "#64748b", fontSize: "13px" }}>
                        {student.rollNumber}
                      </td>
                      {hasRubrics && <td>{renderRubricValue(student)}</td>}
                      {hasRemarks && <td>{renderRemarkValue(student)}</td>}
                    </tr>
                  ))}
                  {studentList.length === 0 && (
                    <tr>
                      <td
                        colSpan={getColumnCount()}
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#94a3b8",
                        }}
                      >
                        No students found for this evaluation.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ─── Footer with Completion Info (NO BUTTONS) ─── */}
        <div
          className="d-flex justify-content-between align-items-center mt-1"
          style={{
            flexShrink: 0,
            backgroundColor: "#fff",
            paddingTop: "12px",
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <div style={{ fontSize: "13px", color: "#64748b" }}>
            <span style={{ fontWeight: 600, color: "#334155" }}>
              Total Students:
            </span>{" "}
            {studentList.length}
            {hasRubrics && (
              <>
                <span
                  style={{
                    marginLeft: "20px",
                    fontWeight: 600,
                    color: "#334155",
                  }}
                >
                  Evaluated:
                </span>{" "}
                {studentList.filter((s) => s.rubricValue).length}
              </>
            )}
            {competencyData?.status === "completed" && (
              <span
                style={{
                  marginLeft: "20px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  color: "#065f46",
                  fontWeight: 600,
                }}
              >
                <CheckCircle size={14} color="#22c55e" />
                Completed
              </span>
            )}
          </div>

          {/* Status indicator - Read Only */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 16px",
              borderRadius: "8px",
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>
              🔒 Read-Only View
            </span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );
}

export default CompletedCompetencyReport;
